import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit3, Trash2, Save, X, MessageSquare } from 'lucide-react';
import { API, adminAuthHeader } from '../api';
import AdminSearch from './AdminSearch';

const empty = { keywords: '', answer: '', order: 0 };

const ChatFAQManager = () => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/chat-faqs`);
      setItems(res.data || []);
    } catch (_) { setItems([]); } finally { setLoading(false); }
  };

  const start = (f) => {
    if (f) {
      setEditingId(f.id);
      setForm({
        keywords: (f.keywords || []).join(', '),
        answer: f.answer || '',
        order: f.order || 0,
      });
    } else {
      setEditingId('new');
      setForm({ ...empty, order: items.length });
    }
  };
  const cancel = () => { setEditingId(null); setForm(empty); };

  const save = async () => {
    if (!form.answer.trim()) return;
    setSaving(true);
    try {
      const payload = {
        keywords: (form.keywords || '').split(',').map((s) => s.trim().toLowerCase()).filter(Boolean),
        answer: form.answer,
        order: Number(form.order) || 0,
      };
      if (editingId === 'new') {
        await axios.post(`${API}/admin/chat-faqs`, payload, { headers: adminAuthHeader() });
      } else {
        await axios.put(`${API}/admin/chat-faqs/${editingId}`, payload, { headers: adminAuthHeader() });
      }
      await load();
      cancel();
    } catch (_) {} finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    try {
      await axios.delete(`${API}/admin/chat-faqs/${id}`, { headers: adminAuthHeader() });
      await load();
    } catch (_) {}
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Chat Bot FAQs</h2>
          <p className="text-sm text-gray-500">Configure what your website chatbot (Sera) answers to common questions. Add trigger keywords and the answer.</p>
        </div>
        <button onClick={() => start(null)} className="flex items-center gap-1.5 bg-[#0d64a8] hover:bg-[#0a548e] text-white text-sm px-4 py-2 rounded-md">
          <Plus size={15} /> New FAQ
        </button>
      </div>

      {editingId && (
        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4">
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Trigger Keywords <span className="text-gray-400">(comma separated, matched anywhere in user message, lowercase)</span></label>
              <input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]" placeholder="e.g. booking, book, reserve" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Answer</label>
              <textarea value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} rows={4} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] resize-none" placeholder="What the chatbot replies" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Order <span className="text-gray-400">(lower = matched first)</span></label>
                <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) || 0 })} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={cancel} className="text-sm text-gray-600 border border-gray-200 rounded-md px-4 py-2">Cancel</button>
              <button onClick={save} disabled={saving} className="text-sm bg-[#0d64a8] text-white rounded-md px-4 py-2 disabled:opacity-70">{saving ? 'Saving...' : 'Save FAQ'}</button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-4">
        <AdminSearch value={q} onChange={setQ} placeholder="Search keywords or answer text..." testId="faq-search" />
      </div>

      {loading ? (
        <div className="text-center py-14 text-sm text-gray-500">Loading FAQs...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-14 bg-white border border-gray-200 rounded-lg">
          <MessageSquare className="mx-auto text-gray-300 mb-2" size={38} />
          <p className="text-gray-600">No FAQs yet. Click “New FAQ” to teach your chatbot its first answer.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.filter((f) => {
            const s = q.trim().toLowerCase();
            if (!s) return true;
            return [(f.keywords || []).join(' '), f.answer].filter(Boolean).join(' ').toLowerCase().includes(s);
          }).map((f) => (
            <div key={f.id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap gap-1 mb-1">
                  {(f.keywords || []).map((k) => (
                    <span key={k} className="text-[10px] bg-[#e8f2fb] text-[#0d64a8] px-1.5 py-0.5 rounded font-mono">{k}</span>
                  ))}
                  <span className="text-[10px] text-gray-400 ml-auto">order {f.order}</span>
                </div>
                <p className="text-sm text-gray-800 leading-relaxed">{f.answer}</p>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <button onClick={() => start(f)} className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#0d64a8] hover:text-[#0d64a8]"><Edit3 size={13}/></button>
                <button onClick={() => remove(f.id)} className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-gray-500 hover:border-red-400 hover:text-red-500"><Trash2 size={13}/></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChatFAQManager;
